import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Users, ShoppingBag, CreditCard, RefreshCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegendContent,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Skeleton } from "@/components/ui/skeleton";

// Types for our statistics
interface AdminStats {
  userStats: {
    totalUsers: number;
    activeUsers: number;
  };
  listingStats: {
    totalListings: number;
    premiumListings: number;
    activeListings: number;
  };
  financialStats: {
    totalPremiumPurchases: number;
    totalCoinsPurchased: number;
  };
  subscriptionStats: {
    basic: number;
    pro: number;
    business: number;
  };
}

// Chart data interfaces
interface ChartData {
  name: string;
  value: number;
}

const DashboardStatistics = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Query to fetch admin statistics
  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ["adminStats"],
    queryFn: async () => {
      // Fetch user statistics
      const { data: users, error: userError } = await supabase
        .from("profiles")
        .select("id, is_blocked, role");

      if (userError) throw new Error("Could not fetch user data");

      // Fetch listing statistics
      const { data: listings, error: listingsError } = await supabase
        .from("listings")
        .select("id, is_premium, is_active");

      if (listingsError) throw new Error("Could not fetch listings data");

      // Fetch premium purchases
      const { data: premiumPurchases, error: premiumError } = await supabase
        .from("premium_payments")
        .select("id");

      if (premiumError) throw new Error("Could not fetch premium data");

      // Fetch subscription data
      const { data: subscriptions, error: subscriptionsError } = await supabase
        .from("subscriptions")
        .select("plan_id")
        .eq("status", "active")
        .gt("expires_at", new Date().toISOString());

      if (subscriptionsError) throw new Error("Could not fetch subscription data");

      // Fetch user credits data
      const { data: credits, error: creditsError } = await supabase
        .from("user_credits")
        .select("purchased_listings_credits");

      if (creditsError) throw new Error("Could not fetch credits data");

      // Calculate total coins purchased
      const totalCoinsPurchased = credits?.reduce(
        (sum, record) => sum + (record.purchased_listings_credits || 0),
        0
      ) || 0;

      // Active users are non-blocked users
      const activeUsers = users?.filter(user => !user.is_blocked).length || 0;
      
      // Premium listings count
      const premiumListings = listings?.filter(listing => listing.is_premium).length || 0;
      
      // Active listings count
      const activeListings = listings?.filter(listing => listing.is_active).length || 0;

      // Count subscriptions by plan
      const basicSubscriptions = subscriptions?.filter(sub => sub.plan_id === 'basic').length || 0;
      const proSubscriptions = subscriptions?.filter(sub => sub.plan_id === 'pro').length || 0;
      const businessSubscriptions = subscriptions?.filter(sub => sub.plan_id === 'business').length || 0;

      return {
        userStats: {
          totalUsers: users?.length || 0,
          activeUsers
        },
        listingStats: {
          totalListings: listings?.length || 0,
          premiumListings,
          activeListings
        },
        financialStats: {
          totalPremiumPurchases: premiumPurchases?.length || 0,
          totalCoinsPurchased
        },
        subscriptionStats: {
          basic: basicSubscriptions,
          pro: proSubscriptions,
          business: businessSubscriptions
        }
      } as AdminStats;
    }
  });

  // Handle refresh button click
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  // Prepare chart data for Subscription Plans
  const subscriptionChartData = stats ? [
    { name: 'Basic', value: stats.subscriptionStats.basic },
    { name: 'Pro', value: stats.subscriptionStats.pro },
    { name: 'Business', value: stats.subscriptionStats.business }
  ] : [];

  // Prepare chart data for Listings
  const listingsChartData = stats ? [
    { name: 'Regular', value: stats.listingStats.totalListings - stats.listingStats.premiumListings },
    { name: 'Premium', value: stats.listingStats.premiumListings }
  ] : [];

  // Loading indicator component
  const LoadingSkeleton = () => (
    <div className="space-y-2">
      <Skeleton className="h-4 w-[250px]" />
      <Skeleton className="h-4 w-[200px]" />
    </div>
  );

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-800 rounded-md">
        <h3 className="font-bold">Error loading statistics</h3>
        <p>{error.message}</p>
        <Button onClick={handleRefresh} className="mt-2" variant="outline">
          <RefreshCcw className="mr-2 h-4 w-4" /> Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Dashboard Statistics</h2>
        <Button
          onClick={handleRefresh}
          variant="outline"
          disabled={isRefreshing}
          className="flex items-center gap-2"
        >
          <RefreshCcw className="h-4 w-4" />
          {isRefreshing ? "Refreshing..." : "Refresh Data"}
        </Button>
      </div>

      {/* User Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.userStats.totalUsers || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats?.userStats.activeUsers || 0} active users
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Listings</CardTitle>
            <ShoppingBag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.listingStats.totalListings || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats?.listingStats.activeListings || 0} active listings
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Premium Listings</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.listingStats.premiumListings || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {((stats?.listingStats.premiumListings || 0) / (stats?.listingStats.totalListings || 1) * 100).toFixed(1)}% of all listings
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Coins Purchased</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.financialStats.totalCoinsPurchased || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats?.financialStats.totalPremiumPurchases || 0} premium purchases
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Subscription Plans</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {isLoading ? (
              <div className="h-full flex items-center justify-center">
                <Skeleton className="h-[90%] w-[90%]" />
              </div>
            ) : (
              <ChartContainer 
                config={{ 
                  basic: { color: "#4ade80" }, 
                  pro: { color: "#60a5fa" }, 
                  business: { color: "#f43f5e" } 
                }}
              >
                <BarChart data={subscriptionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Legend content={<ChartLegendContent />} />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Listings Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {isLoading ? (
              <div className="h-full flex items-center justify-center">
                <Skeleton className="h-[90%] w-[90%]" />
              </div>
            ) : (
              <ChartContainer 
                config={{ 
                  regular: { color: "#94a3b8" }, 
                  premium: { color: "#f59e0b" }
                }}
              >
                <BarChart data={listingsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Legend content={<ChartLegendContent />} />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardStatistics;
